import { filterPropertyTypes } from 'boot/globals';

/**
 * Whether a criterion's chosen values must all be present, or any one of them.
 *
 * This is not a new concept and nothing is stored to express it. The filter engine has always read
 * both shapes:
 *
 *   one statement holding ['a','b']          the element has a OR b
 *   two statements holding ['a'] and ['b']   the element has a AND b
 *
 * because it requires every statement to match and any value within a statement to match. So the
 * mode is a decision about which shape to write, and is read back from the shape that is there.
 * FiltersMixin and FilterUtils are untouched, and filters saved before this keep their meaning.
 */

export const MATCH_MODES = {
  /** Any one of the chosen values is enough. One statement holding all of them. */
  ANY: 'any',
  /** Every chosen value must be present. One statement per value. */
  ALL: 'all',
};

/*
 * Only array-valued criteria can mean "all". For a scalar the matcher asks whether the element's
 * one value is in the statement's list, so two single-value statements would require it to be two
 * things at once and match nothing at all.
 */
const ARRAY_VALUED = [
  filterPropertyTypes.ArrayInArray,
  filterPropertyTypes.ArrayContainsUser,
];

/**
 * Whether the choice is meaningful for this criterion.
 *
 * The filter type says the element holds an array, which is necessary but not sufficient: a
 * criterion can be stored as an array and still only ever hold one value, and "all" over a
 * one-element array matches nothing. Those declare `singleValued`, since only the field's owner
 * knows the difference.
 *
 * @param {{ filterType: string, options?: { singleValued?: boolean } }} criterion
 * @returns {boolean}
 */
export function supportsMatchMode(criterion) {
  return ARRAY_VALUED.includes(criterion?.filterType)
    && criterion?.options?.singleValued !== true;
}

/**
 * Whether a stored filter's statements are a shape the editor can hold.
 *
 * Two are: one statement carrying any number of values, which is "any", and one value per
 * statement, which is "all". A filter holding several statements that each carry several values is
 * neither: ['a','b'] and ['c','d'] means (a OR b) AND (c OR d), and a single selection plus a
 * binary mode cannot express that grouping. Read as "all" and written back a value at a time it
 * would become a AND b AND c AND d, which is a different filter.
 *
 * @param {{ filterStatements?: object[] }} [filter]
 * @returns {boolean}
 */
export function isGatheredShape(filter) {
  const stored = filter?.filterStatements ?? [];

  return stored.length <= 1
    || stored.every((statement) => Array.isArray(statement?.filteredItems)
      && statement.filteredItems.length <= 1);
}

/**
 * The mode a stored filter is already in, read from its shape. More than one statement is what
 * "all" looks like, provided the statements are a shape the editor can hold at all.
 *
 * @param {{ filterStatements?: object[] }} [filter]
 * @returns {string}
 */
export function getMatchMode(filter) {
  return isGatheredShape(filter) && (filter?.filterStatements?.length ?? 0) > 1
    ? MATCH_MODES.ALL
    : MATCH_MODES.ANY;
}

/**
 * Every value a stored filter selects, however its statements are arranged. Lets the editor show
 * one set of chosen values whichever mode produced them.
 *
 * Deduplicated, because match-all is the shape repeatedly adding the same filter used to produce
 * by hand, so a stored filter can carry the same value in two statements. The editors render one
 * chip per entry keyed by value, so a duplicate is a repeated key in the v-for.
 *
 * @param {{ filterStatements?: object[] }} [filter]
 * @returns {unknown[]}
 */
export function getSelectedValues(filter) {
  return [...new Set((filter?.filterStatements ?? [])
    .flatMap((statement) => (Array.isArray(statement?.filteredItems)
      ? statement.filteredItems
      : [])))];
}

/**
 * Arrange one statement's values into the statements to store.
 *
 * "All" writes one statement per value, which is what repeatedly adding the same filter used to
 * produce by hand. "Any" writes the single statement it was given.
 *
 * @param {object} statement The statement the editor built
 * @param {string} mode
 * @returns {object[]}
 */
export function toStatements(statement, mode) {
  const values = Array.isArray(statement?.filteredItems) ? statement.filteredItems : null;

  if (mode !== MATCH_MODES.ALL || values === null || values.length < 2) {
    return [statement];
  }

  return values.map((value) => ({ ...statement, filteredItems: [value] }));
}
